import { createFileRoute, Outlet, Navigate } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth/auth-provider";
import { AppLayout } from "@/components/app-layout";
import { AppLoading } from "@/components/app-loading";
import { CreateFirstUserModal } from "@/components/dashboard/create-first-user-modal";

export const Route = createFileRoute("/_app")({
  component: AppLayoutRoute,
});

const SUBSCRIBE_REQUIRED_STATUSES = new Set(["pending", "canceled", "expired"]);

function AppLayoutRoute() {
  const {
    isAuthenticated,
    isLoading,
    user,
    pharmacy,
    needsFirstUser,
    needsLegalAcceptance,
    subscriptionStatus,
  } = useAuth();

  if (isLoading) return <AppLoading />;

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (
    subscriptionStatus &&
    SUBSCRIBE_REQUIRED_STATUSES.has(subscriptionStatus)
  ) {
    return <Navigate to="/subscribe" />;
  }

  if (needsLegalAcceptance) {
    return <Navigate to="/accept-terms" />;
  }

  // Fresh pharmacy with no staff users yet — render the create-first-user
  // modal directly and do NOT mount <AppLayout>. AppLayout wraps its children
  // in providers (RemindersProvider, etc.) that eagerly fetch user-scoped
  // endpoints on mount. Those requests 401 with NO_ACTIVE_USER, which the
  // api client auto-redirects to /select-user, which bounces right back to
  // /dashboard because needsFirstUser=true — a tight /dashboard ↔ /select-user
  // navigation loop for every fresh signup.
  if (needsFirstUser) {
    return (
      <CreateFirstUserModal open={true} pharmacyName={pharmacy?.name ?? ""} />
    );
  }

  if (!user) {
    return <Navigate to="/select-user" />;
  }

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}
